import React, { useState, useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import GoldDivider from '../components/GoldDivider';
import API_URL from '../config.js';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function Guestbook() {
  const { t } = useLanguage();

  const [form, setForm] = useState({ name: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);

  // Load approved messages on mount
  useEffect(() => {
    async function fetchMessages() {
      setMessagesLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/guestbook`);
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setMessages(data);
      } catch {
        // silently fail — messages list will just be empty
      } finally {
        setMessagesLoading(false);
      }
    }
    fetchMessages();
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.message.trim()) {
      setError(t('rsvp.required_fields'));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/guestbook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), message: form.message.trim() }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || t('guestbook.error'));
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || t('guestbook.error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-container" style={{ paddingTop: '2rem' }}>
      <h1 className="page-title">{t('guestbook.title')}</h1>
      <p className="page-subtitle">{t('guestbook.subtitle')}</p>
      <GoldDivider />

      {/* Submission form */}
      <div className="card" style={{ maxWidth: '600px', margin: '0 auto 3rem' }}>
        {submitted ? (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>💌</div>
            <p style={{ color: 'var(--gold-light)', fontWeight: 600 }}>{t('guestbook.success')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="gb-name">{t('guestbook.name')}</label>
              <input
                id="gb-name"
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                autoComplete="name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="gb-message">{t('guestbook.message')}</label>
              <textarea
                id="gb-message"
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                rows={4}
              />
            </div>
            {error && (
              <div className="error-box" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}
            <div style={{ textAlign: 'center' }}>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
                style={{ opacity: loading ? 0.7 : 1 }}
              >
                {loading ? t('common.loading') : t('guestbook.submit')}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Messages list */}
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        <h2
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: 'var(--gold)',
            fontSize: '1.5rem',
            marginBottom: '1.5rem',
            textAlign: 'center',
          }}
        >
          {t('guestbook.messages_title')}
        </h2>

        {messagesLoading ? (
          <p style={{ textAlign: 'center', color: 'rgba(245,233,198,0.5)' }}>{t('common.loading')}</p>
        ) : messages.length === 0 ? (
          <div
            className="card"
            style={{ textAlign: 'center', color: 'rgba(245,233,198,0.5)', fontStyle: 'italic', padding: '2rem' }}
          >
            {t('guestbook.no_messages')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className="gb-entry"
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                    flexWrap: 'wrap',
                    gap: '0.25rem',
                  }}
                >
                  <span className="gb-name">
                    {msg.name}
                  </span>
                  <span className="gb-date">
                    {formatDate(msg.created_at)}
                  </span>
                </div>
                <p className="gb-text">{msg.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
